import { formatSubscriptionPause } from "./subscriptionPause";
import uuid from "react-uuid";
import { clearNumber, dateFormat, dateFormatedToDB, formatMoney, formatName, getSubscriptionCycleErrorReasonMessage, getSchedulingDepartmentStatusCode, getSubscriberStatusColor, getSubscriberStatusText, splitName, stringDateFormat, getSubscriptionTransactionStatusColor, getSubscriptionTransactionStatusText, getSubscriptionTransactionStatusBadgeClass, getSubscriptionTransactionMethodText, getSubscriptionTransactionMethodColor, getSubscriptionTransactionMethodBadgeClass, getSubscriptionTransactionTypeText, getSubscriptionTransactionTypeColor } from "../functions";
import moment from "moment";
import slugify from "slugify";
import _ from "lodash";
import { BATH_TEXT } from "../../styles/constant";
import { DRE_EXPENSES, DRE_GOALS, DRE_RESPONSE_BLOCKS, PAYMENT_ERROR_REASONS, TRANSACTION_PROCESSING_ERRORS } from "../../store/subscription/constants";

const PIX_PAYMENT_TEXT = 'Pagamento via PIX';
const EMPTY_CYCLE_PAYMENT_TEXT = '—';

function normalizePaymentValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function isPixCyclePayment(value) {
  return normalizePaymentValue(value) === PIX_PAYMENT_TEXT;
}

function isMaskedCardCyclePayment(value) {
  return /^\*\*\*\* \*\*\*\* \*\*\*\* \d+$/.test(normalizePaymentValue(value));
}

function formatCyclePayment(cycle = {}) {
  const paidWith = normalizePaymentValue(cycle?.paidWith);

  if (isPixCyclePayment(paidWith) || isMaskedCardCyclePayment(paidWith)) {
    return paidWith;
  }

  const paymentMethod = normalizePaymentValue(cycle?.paymentMethod).toLowerCase();
  if (paymentMethod === 'pix') return PIX_PAYMENT_TEXT;

  const lastDigits = normalizePaymentValue(cycle?.lastFourDigits || cycle?.lastDigits);
  if (lastDigits) return `**** **** **** ${lastDigits}`;

  return EMPTY_CYCLE_PAYMENT_TEXT;
}

function formatInvoice(invoice) {
  const split = {
    active: !!invoice?.split ? invoice.split.active : false,
    affiliateId: !!invoice?.split ? invoice.split.affiliateId : '',
    amount: !!invoice?.split ? invoice.split.amount : 0,
  }

  return {
    split,
    id: invoice.id || uuid(),
    billingAt: invoice.billingAt,
    paidAmount: invoice.paidAmount,
    paidNet: clearNumber(invoice.paidNet.toFixed(2)),
    fee: clearNumber(invoice.fee.toFixed(2)),
    description: invoice.description,
    customer: {
      name: invoice.customerName,
      cpf: invoice.cpf,
      pet: invoice.petName,
      planType: invoice.planType,
    },
    full: invoice,
  };
}

function formatAvailablePlan(availablePlan) {
  return {
    id: availablePlan._id,
    cluster: availablePlan.cluster,
    code: availablePlan.code,
    description: availablePlan.description,
    price: availablePlan.price,
  };
}

function formatCycle(cycle) {
  const customerName = splitName(`${cycle.customerFirstName} ${cycle.customerLastName}`)

  return {
    id: cycle._id,
    appointment: {
      date: dateFormat(cycle.appointmentDate),
    },
    customer: {
      name: customerName
    },
    pet: {
      id: cycle.petId,
      name: formatName(cycle.petName)
    },
    service: {
      status: getSchedulingDepartmentStatusCode(cycle.status),
      name: formatName(cycle.serviceName),
      price: {
        original: cycle.originalPrice,
        totalPrice: cycle.totalPrice,
      },
    },
    subscription: {
      planId: cycle.subscriptionPlanId,
    },
  };
}

function formatTransaction(transaction) {
  const transactionCustomer = {
    id: transaction.customer.id,
    name: formatName(transaction.customer.name),
    email: transaction.customer.email,
  }

  const transactionPet = {
    id: transaction.pet.id,
    name: formatName(transaction.pet.name),
  }



  const transactionPaymentMethod = {
    code: transaction.payment.method.code,
    text: getSubscriptionTransactionMethodText(transaction.payment.method.code),
    color: getSubscriptionTransactionMethodColor(transaction.payment.method.code),
  }


  const transactionPayment = {
    ...transaction.payment,
    method: transactionPaymentMethod,
  }

  const transactionGateway = {
    id: transaction.gateway.transactionId,
    code: transaction.gateway.responseCode,
    message: transaction.gateway.message,
  }



  if (transactionPayment.method.code === "credit_card") {
    if (_.includes(TRANSACTION_PROCESSING_ERRORS, transaction.transactionStatus)) {
      transactionGateway.message = getSubscriptionCycleErrorReasonMessage(transaction.gateway.responseCode, transaction.payment.card.brand)
    }
  }

  if (transaction.transactionType === 'refund') {
    if (transaction.transactionStatus === 'success') transaction.transactionStatus = 'refund'
  }

  if (transactionPayment.method.code === "pix") {
    if (transaction.transactionType === 'void') transaction.transactionStatus = 'canceled'
  }



  const transactionStatus = {
    code: transaction.transactionStatus,
    text: getSubscriptionTransactionStatusText(transaction?.transactionStatus),
    color: getSubscriptionTransactionStatusColor(transaction?.transactionStatus),
  }

  const transactionType = {
    code: transaction.transactionType,
    text: getSubscriptionTransactionTypeText(transaction?.transactionType),
    color: getSubscriptionTransactionTypeColor(transaction?.transactionType),
  }

  return {
    id: transaction._id,
    amount: formatMoney(transaction.amount / 100),
    cycleId: transaction.cycleId,
    chargeId: transaction.chargeId,
    attempts: transaction.attempts,
    invoiceId: transaction.invoiceId,
    transactionId: transaction.transactionId,
    subscriptionId: transaction.subscriptionId,
    type: transactionType,
    status: transactionStatus,
    pet: transactionPet,
    gateway: transactionGateway,
    payment: transactionPayment,
    customer: transactionCustomer,
    transactionDate: moment(transaction.transactionDate).format('DD/MM/YY'),
    transactionHour: moment(transaction.transactionDate).format('HH:mm:ss'),
  }
}

function formatSubscriber(subscribe) {
  const saller = !!subscribe.seller ? subscribe.seller.name : '-'

  // Usa o último ciclo PAGO para calcular banhos disponíveis.
  // Quando há falha seguida de pagamento no mesmo mês, ambos têm o mesmo billingAt —
  // sem filtrar por status, _.maxBy pode retornar o ciclo com falha (quantityAvailable: 0).
  // Fix: chamado 86ahecvft
  const lastPaidCycle = _.maxBy(
    subscribe.cycle.filter(c => c.status === 'paid'),
    (cycle) => moment(cycle.billingAt)
  );
  const lastCycle = lastPaidCycle || _.maxBy(subscribe.cycle, (cycle) => moment(cycle.billingAt));
  const avaliableBath = !!lastCycle ? lastCycle.quantityAvailable || 0 : 0;
  const avaliableBathText = avaliableBath === 1 ? BATH_TEXT[0] : BATH_TEXT[1]

  const isCanceled = !!subscribe?.canceled_at || subscribe?.subscriptionStatus === "Cancelado"
  const isPaused = subscribe?.subscriptionStatus === "Pausado" || subscribe?.pausa?.pausado === true

  const createdAt = moment(stringDateFormat(subscribe.createdAt), 'DD/MM/YYYY').format('DD/MM/YY')
  const canceledAt = !!subscribe?.canceled_at ? moment(stringDateFormat(subscribe.canceled_at), 'DD/MM/YYYY').format('DD/MM/YY') : ''
  const nextBillingAt = !isCanceled ? moment(stringDateFormat(subscribe.nextBillingAt), 'DD/MM/YYYY').format('DD/MM/YY') : ''
  const validUntilDate = !!subscribe?.validUntilDate ? moment(stringDateFormat(subscribe.validUntilDate), 'DD/MM/YYYY').subtract(1, 'days').format('DD/MM/YY') : ''

  const validAt = !!subscribe?.validUntilDate ? validUntilDate : moment(nextBillingAt, 'DD/MM/YYYY').subtract(1, 'days').format('DD/MM/YY')

  const customerName = splitName(subscribe.customerFullName)

  const cart = {
    firstDigits: subscribe?.card?.firstSixDigits || '',
    lastDigits: subscribe?.card?.lastFourDigits || '',
    brand: subscribe?.card?.brand || '',
  }


  const priceAdjustment = subscribe?.priceAdjustment?.map((price) => ({
    id: price?._id,
    cycle: price?.cycle,
    type: price?.type,
    value: price?.value,
    newPrice: price?.newPrice ? formatMoney(+price?.newPrice) : '-',
    nextBillingAt: price?.nextBillingAt ? stringDateFormat(price?.nextBillingAt) : '-',
  }))

  const queueJobs = subscribe?.queueJobs?.map((job) => ({
    id: job?._id,
    jobName: job?.jobName,
    jobStatus: job?.jobStatus,
    adjustmentValue: job?.data?.adjustmentValue,
    adjustment: job?.data?.adjustment,
    nextBilling: job?.data?.nextBilling,
    effectiveBillingDate: job?.effectiveBillingDate ?? job?.data?.effectiveBillingDate,
    estimatedBillingDate: job?.data?.estimatedBillingDate,
    effectiveBillingEstimated: job?.effectiveBillingEstimated ?? (!job?.data?.effectiveBillingDate && !!job?.data?.estimatedBillingDate),
    effectiveBillingLabel: job?.effectiveBillingLabel || 'Data indisponível',
    ruleVersion: job?.data?.ruleVersion,
    processFromDate: job?.data?.processFromDate ? stringDateFormat(job?.data?.processFromDate) : '-',
    createdAt: job?.createdAt ? stringDateFormat(job?.createdAt) : '-',
    raw: job,
  }))


  const cycles = subscribe.cycle.map((cycle) => ({
    data: cycle,
    id: cycle._id,
    billingAt: stringDateFormat(cycle.billingAt),
    message: cycle.status === 'paid' ? '-' : PAYMENT_ERROR_REASONS.generic,
    acquirerCode: cycle?.acquirerCode,
    errorReasonMessage: !!cycle?.acquirerCode ? getSubscriptionCycleErrorReasonMessage(cycle.acquirerCode.toString(), cycle.lastTransactionBody.payment_profile.payment_company.code) : "",
    paidAmount: formatMoney(+cycle.paidAmount / 100),
    chargeId: cycle.chargeId,
    card: subscribe.card,
    cycle: cycle.cycle,
    invoiceId: cycle.invoiceId,
    quantityAvailable: cycle.quantityAvailable,
    status: {
      code: cycle.status,
      color: getSubscriberStatusColor(cycle.status),
      text: getSubscriberStatusText(cycle.status)
    },
    paidWith: normalizePaymentValue(cycle?.paidWith),
    payment: formatCyclePayment(cycle),
    firstDigits: cycle?.firstSixDigits || '',
    lastDigits: cycle?.lastFourDigits || '',
    brand: cycle?.lastTransactionBody?.payment_profile?.payment_company?.code || '',
  }))

  return {
    id: subscribe._id || uuid(),
    status: subscribe.subscriptionStatus,
    cart,
    validAt,
    cycles,
    saller,
    createdAt,
    canceledAt,
    isCanceled,
    isPaused,
    ...formatSubscriptionPause(subscribe),
    nextBillingAt,
    validUntilDate,
    // customer.email usado em AlertChangeCard; origem: API GET subscriptions/store/list/:id → data.subscription
    customer: {
      id: subscribe?.customerId || '',
      name: customerName,
      cpf: subscribe.cpf,
      email: (subscribe?.customer?.email ?? subscribe?.customerEmail ?? subscribe?.email ?? '').toString().trim(),
    },
    pet: {
      id: subscribe.petId,
      name: formatName(subscribe.petName),
      size: subscribe.validPetSizes,
    },
    plan: {
      id: subscribe?.planId || '',
      price: subscribe.price,
      type: subscribe.planType,
      price: subscribe.price,
      avaliableBath,
      avaliableBathText
    },
    priceAdjustment,
    queueJobs
  };
}

function formatPagination(pagination) {
  return {
    page: pagination.currentPage,
    totalPages: pagination.totalPages,
    perPage: pagination.limit,
    totalDocuments: pagination.totalDocuments,
    full: pagination,
  };
}

function formatResultCalculation(result) {
  return {
    expenses: result.despesas,
    revenue: result.receita,
    result: result.resultado,
  };
}

function formatPricing(data) {
  const table = _.mapValues(_.groupBy(data.tabela, 'porte'), arr => _.sortBy(arr, 'plano'));

  return {
    recommendation: data.fatorRecomendacao,
    percentageCorrect: data.porcentagemCorretos,
    table
  };
}

function formatCustomExpenses(customExpenses) {
  const expenses = {}

  _.forEach(DRE_EXPENSES, (expense, key) => {
    const customExpense = customExpenses.find((data) => data.nomeDespesa === expense.id)
    expenses[key] = {
      value: customExpense.valorDespesa || '',
      default: customExpense.default || '',
    }
  })

  return expenses
}

function formatCustomGoals(customGoals) {
  const goals = {}

  _.forEach(DRE_GOALS, (goal, key) => {
    const customGoal = _.find(customGoals, (data) => data.nomeMeta === goal.id)
    goals[key] = {
      value: customGoal.valorMeta || '',
      default: customGoal.default || '',
    }
  })

  return goals
}

function formatDre(data) {
  let columns = []

  const blocks = {}
  const blocksColumns = {}

  const blocksDetails = {}
  const blocksDetailsColumns = {}

  const dres = _.concat([], data)

  const makeColumn = (key, dre) => {
    let columnValue = key

    _.forEach(blocks, (block) => {
      if (!!block.column) {
        const columnData = dre[block.column]
        columnValue = _.isNumber(columnData) ? columnData : _.sumBy(columnData, 'valor');
      }

      blocksColumns[block.id][key] = {
        id: `${block.id}-${key}`,
        value: block.format(columnValue)
      }
    })
  }

  const makeBlockDetailsColumn = (key, dre) => {
    _.forEach(blocks, (block) => {
      if (!!block.column && _.isArray(dre[block.column])) {
        blocksDetailsColumns[block.id][key] = {}

        _.forEach(dre[block.column], (detail) => {
          const id = slugify(detail.nome, { lower: true })

          blocksDetailsColumns[block.id][key][id] = {
            id: `${block.id}-${key}-${id}`,
            value: block.format(detail.valor)
          }
        })
      }
    })
  }

  const makeBlockDetails = (dre) => {
    _.forEach(blocks, (block) => {
      if (!!block.column && _.isArray(dre[block.column])) {
        blocksDetails[block.id] = []

        _.forEach(dre[block.column], (detail) => {
          const id = slugify(detail.nome, { lower: true })

          blocksDetails[block.id].push({
            id,
            title: detail.nome
          })
        })
      }
    })
  }

  _.forEach(DRE_RESPONSE_BLOCKS, (value, id) => {
    const block = { id, ...value }

    blocks[id] = block
    blocksColumns[id] = {}
    blocksDetailsColumns[id] = {}
  })

  _.forEach(dres, (dre) => {
    const key = dateFormatedToDB(stringDateFormat(dre.data))
    columns = _.union(columns, [key]);

    makeBlockDetails(dre)

    makeColumn(key, dre)
    makeBlockDetailsColumn(key, dre)
  })

  return {
    blocks: _.omitBy(blocks, _.isEmpty),
    columns: _.compact(columns),
    blocksColumns: _.omitBy(blocksColumns, _.isEmpty),
    blocksDetails: _.omitBy(blocksDetails, _.isEmpty),
    blocksDetailsColumns: _.omitBy(blocksDetailsColumns, _.isEmpty),
  }
}

export const format = {
  pagination: (pagination) => formatPagination(pagination),
  invoice: (invoice) => formatInvoice(invoice),
  invoices: (invoices) => {
    if (!invoices.length) return [];
    return invoices.map(formatInvoice);
  },
  subscriber: (subscriber) => formatSubscriber(subscriber),
  subscribers: (subscribers) => {
    if (!subscribers.length) return [];
    return subscribers.map(formatSubscriber);
  },
  cycle: (cycle) => formatCycle(cycle),
  cycles: (cycles = []) => cycles.map(formatCycle),
  transaction: (transaction) => formatTransaction(transaction),
  transactions: (transactions = []) => transactions.map(formatTransaction),
  availablePlan: (availablePlan) => formatAvailablePlan(availablePlan),
  availablePlans: (availablePlans) => {
    if (!availablePlans.length) return [];
    return availablePlans.map(formatAvailablePlan);
  },
  resultCalculation: (result) => formatResultCalculation(result),
  pricing: (data) => formatPricing(data),
  dre: (dre) => formatDre(dre),
  customExpenses: (expenses) => formatCustomExpenses(expenses),
  customGoals: (goals) => formatCustomGoals(goals)
};
